import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";

// Stored content (webinars, tutoring, rewards, accounting notes) is rendered
// with rehypeRaw, which turns author-supplied HTML into real elements. Without
// a sanitizer that means arbitrary HTML written by one user rendering inside
// another user's page, so everything raw goes through this schema afterwards.
//
// rehype-sanitize is allowlist-based: anything not named here is dropped.

const ALLOWED_PROTOCOLS = ["http", "https", "mailto"];

// Already absent from defaultSchema — listed as an explicit record of what
// must never be added back.
const NEVER_ALLOWED = [
  "script",
  "style",
  "iframe",
  "object",
  "embed",
  "form",
  "input",
  "button",
  "textarea",
  "select",
  "option",
  "svg",
  "math",
  "link",
  "meta",
  "base",
];

export const markdownSanitizeSchema = {
  ...defaultSchema,

  // Block dangerous URL schemes (javascript:, vbscript:, data:) everywhere a
  // URL can appear. Note react-markdown independently applies its own
  // urlTransform after these plugins, which permits only http(s)/mailto/irc/
  // xmpp — so data: URIs never render even if a schema allowed them.
  protocols: {
    ...defaultSchema.protocols,
    href: ALLOWED_PROTOCOLS,
    cite: ALLOWED_PROTOCOLS,
    longDesc: ALLOWED_PROTOCOLS,
    src: ["http", "https"],
  },

  tagNames: (defaultSchema.tagNames || []).filter(
    (tag) => !NEVER_ALLOWED.includes(tag),
  ),

  attributes: {
    ...defaultSchema.attributes,
    // No event handlers and no inline styles on any element.
    "*": (defaultSchema.attributes?.["*"] || []).filter(
      (attr) =>
        typeof attr !== "string" ||
        (attr.toLowerCase() !== "style" && !attr.toLowerCase().startsWith("on")),
    ),
    a: [
      ...(defaultSchema.attributes?.a || []),
      // Forced rather than trusted from the author's markup.
      ["target", "_blank"],
      ["rel", "noopener noreferrer nofollow"],
    ],
    img: ["src", "alt", "title", "width", "height", "loading"],
  },
};

// The only supported way to render stored HTML. Exported as a pair so raw HTML
// can never be switched on without the sanitizer running after it — order
// matters, rehypeRaw builds the nodes and the sanitizer then prunes them.
export const rawMarkdownRehypePlugins = [
  rehypeRaw,
  [rehypeSanitize, markdownSanitizeSchema],
];
